import { useLazyListStoresQuery } from 'api/client-domain/client-domain';
import { usePostMenusQuery } from 'api/ordering-service/ordering-service';
import { useSearch } from 'components/search/search.hooks';
import {
  defaultSearchParams,
  querySearchParam,
  stringListSearchParam,
  stringSearchParam,
} from 'components/search/search.utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const useMenusSearch = (isNative = false) => {
  const { t } = useTranslation();
  const [triggerStores] = useLazyListStoresQuery();

  const searchParams = useMemo(
    () => [
      ...defaultSearchParams({
        count: 10,
        page: 0,
        sortBy: 'title',
        sortDirection: 'ASC',
      }),
      querySearchParam('title', {
        category: t('MenusDataGrid.filter.title'),
      }),
      querySearchParam('originalTitle', {
        category: t('MenusDataGrid.filter.externalTitle'),
      }),
      querySearchParam('incentivioIds', {
        category: t('MenusDataGrid.filter.id'),
      }),
      querySearchParam('externalIds', {
        category: t('MenusDataGrid.filter.externalId'),
      }),
      stringSearchParam('includeStores', {
        defaultValue: 'true',
      }),
      stringSearchParam('imported', {
        defaultValue: isNative ? 'false' : 'true',
      }),
      stringListSearchParam('storeIds', {
        defaultValue: ['ALL'],
        options: [
          {
            label: t('MenusDataGrid.filter.locations.allOption'),
            id: 'ALL',
          },
        ],
        asyncOptions: async () => {
          const stores = await triggerStores({ count: 300, page: 0 });

          const dataArray = stores?.data?.stores.map(store => ({
            label: store.title,
            id: store.storeId,
          }));

          return [
            {
              label: t('MenusDataGrid.filter.locations.allOption'),
              id: 'ALL',
            },
          ].concat(dataArray ?? []);
        },
        filter: {
          type: 'MultiSelectFilter',
          label: t('MenusDataGrid.filter.locations.label'),
        },
      }),
    ],
    [isNative, t, triggerStores],
  );

  const modifiedSearchParams = useMemo(() => {
    if (isNative) {
      return searchParams.filter(
        ({ paramName }) =>
          paramName !== 'originalTitle' && paramName !== 'externalIds',
      );
    }
    return searchParams;
  }, [isNative, searchParams]);

  return useSearch(usePostMenusQuery, modifiedSearchParams);
};
